/**
 * Koshirae Quote / Contact Modal — Tokyo Nihonto
 * ------------------------------------------------
 * Lets a customer save their full configuration and send it as an inquiry
 * (via Shopify's native contact form) without paying upfront.
 *
 * - Reads the live configuration straight from the configurator DOM, so it
 *   stays in sync with whatever the customer selected.
 * - Renders a readable summary inside the modal.
 * - Mirrors the same data into hidden contact[] fields so the full spec is
 *   emailed to the shop's contact address. The customer's email becomes the
 *   reply-to, so the merchant can answer directly.
 *
 * No backend, no third-party service, no API keys.
 */
(function () {
  'use strict';

  function $(id) { return document.getElementById(id); }

  /* ─────────────────────────────────────────────
     COLLECT CONFIGURATION FROM THE DOM
  ───────────────────────────────────────────── */
  function isHidden(el) {
    // Hidden because inside a collapsed conditional block
    var cond = el.closest('.kc-cond');
    if (cond && !cond.classList.contains('is-visible')) return true;
    // Hidden because inside the uchigatana-only options that are display:none
    var uchi = el.closest('#kc-uchi-opts');
    if (uchi && uchi.offsetParent === null) return true;
    return false;
  }

  function labelForRadio(el) {
    var label = el.closest('label');
    if (!label) return el.value;
    var txt = label.querySelector('.kc-radio-text') ||
              label.querySelector('.kc-groove-name') ||
              label.querySelector('.grid-label');
    return txt ? txt.textContent.replace(/\s+/g, ' ').trim() : el.value;
  }

  // Returns an array of { key, value } in DOM order, de-duplicated by key.
  function collectConfig() {
    var items = [];
    var seen = {};
    var controls = document.querySelectorAll('.kc-configurator [name^="properties["]');

    controls.forEach(function (el) {
      if (isHidden(el)) return;

      var key = el.getAttribute('name').replace('properties[', '').replace(']', '');
      var value = '';

      if (el.type === 'radio') {
        if (!el.checked) return;
        value = labelForRadio(el);
      } else if (el.tagName === 'SELECT') {
        var opt = el.options[el.selectedIndex];
        value = opt ? opt.text.trim() : '';
      } else {
        value = (el.value || '').trim();
      }

      if (!value) return;
      if (seen[key]) return;
      seen[key] = true;
      items.push({ key: key, value: value });
    });

    return items;
  }

  function getTotal() {
    var el = $('kc-summary-total') || $('kc-total');
    return el ? el.textContent.trim() : '';
  }

  /* ─────────────────────────────────────────────
     RENDER SUMMARY INTO THE MODAL + HIDDEN FIELDS
  ───────────────────────────────────────────── */
  function populate() {
    var items = collectConfig();
    var total = getTotal();

    // Visible list
    var list = $('kc-quote-config-list');
    if (list) {
      list.innerHTML = '';
      if (!items.length) {
        var empty = document.createElement('li');
        empty.className = 'kc-quote-config-empty';
        empty.textContent = 'No options selected yet — make your choices, then send your request.';
        list.appendChild(empty);
      } else {
        items.forEach(function (item) {
          var li = document.createElement('li');
          var k = document.createElement('span');
          k.className = 'kc-qc-key';
          k.textContent = item.key;
          var v = document.createElement('span');
          v.className = 'kc-qc-val';
          v.textContent = item.value;
          li.appendChild(k);
          li.appendChild(v);
          list.appendChild(li);
        });
      }
    }

    var totalEl = $('kc-quote-total');
    if (totalEl && total) totalEl.textContent = total;

    // Hidden fields mirrored into the contact email
    var lines = items.map(function (i) { return '• ' + i.key + ': ' + i.value; });
    var summaryText = (total ? 'Estimated Total: ' + total + '\n\n' : '') + lines.join('\n');

    var cfgField = $('kc-quote-config-field');
    if (cfgField) cfgField.value = summaryText;
    var totalField = $('kc-quote-total-field');
    if (totalField) totalField.value = total;
  }

  /* ─────────────────────────────────────────────
     OPEN / CLOSE
  ───────────────────────────────────────────── */
  var overlay, lastFocus;

  function open() {
    if (!overlay) return;
    populate();
    lastFocus = document.activeElement;
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    var firstInput = $('kc-quote-name');
    if (firstInput) setTimeout(function () { firstInput.focus(); }, 50);
  }

  function close() {
    if (!overlay) return;
    overlay.hidden = true;
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ─────────────────────────────────────────────
     BOOT
  ───────────────────────────────────────────── */
  function init() {
    overlay = $('kc-quote-overlay');
    if (!overlay) return;

    var openBtn = $('kc-quote-btn');
    var closeBtn = $('kc-quote-close');

    if (openBtn) openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Click on the backdrop (but not the panel) closes
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });

    // ESC closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });

    // Make sure hidden fields are fresh right before the native submit
    var form = $('kc-quote-form');
    if (form) form.addEventListener('submit', populate);

    // Auto-open on success / error redirect (Shopify reloads with ?contact_posted=true)
    var posted = /[?&]contact_posted=true/.test(window.location.search);
    var hasState = overlay.querySelector('.kc-quote-success, .kc-quote-errors');
    if (posted || hasState) open();

    setupBreakdownPopover();
  }

  /* ─────────────────────────────────────────────
     PRICE BREAKDOWN POPOVER
     The configurator JS re-renders the breakdown items on every change. We
     keep a synced "Total" footer inside the popover (the big total in the bar
     is hidden) and close the popover on outside click.
  ───────────────────────────────────────────── */
  function setupBreakdownPopover() {
    var bd = $('kc-summary-breakdown');
    var toggle = $('kc-breakdown-toggle');
    if (!bd) return;

    var totalEl = $('kc-summary-total'); // hidden, but still updated by configurator JS
    var scheduled = false;
    var observer;

    function syncTotalRow() {
      if (!totalEl) return;
      observer.disconnect();
      var old = bd.querySelector('.kc-bd-total');
      if (old) old.remove();
      if (bd.children.length) {
        var row = document.createElement('div');
        row.className = 'kc-bd-item kc-bd-total';
        var label = document.createElement('span');
        label.textContent = 'Total';
        var price = document.createElement('span');
        price.className = 'kc-bd-price';
        price.textContent = totalEl.textContent.trim();
        row.appendChild(label);
        row.appendChild(price);
        bd.appendChild(row);
      }
      observer.observe(bd, { childList: true });
    }

    observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        syncTotalRow();
      });
    });
    observer.observe(bd, { childList: true });
    syncTotalRow();

    // Close the popover when clicking outside it (reuses the toggle's own
    // handler so its label stays in sync)
    if (toggle) {
      document.addEventListener('click', function (e) {
        if (!bd.classList.contains('is-open')) return;
        if (bd.contains(e.target) || toggle.contains(e.target)) return;
        toggle.click();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
